import React, { useState, useEffect } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Text, Flex, Button, Table, Box, Group, TextInput, LoadingOverlay, Pagination, ActionIcon, Tooltip } from '@mantine/core'
import { IconPlus, IconSearch, IconEdit, IconTrash } from '@tabler/icons-react'
import { getAllCurrencyList, deleteCurrency } from '../../../../services/currency'
import { modalDeleteData } from '../../../../component/ui/Prompt/modalDeleteData'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { notifications } from '@mantine/notifications'
import ModalCurrency from './ModalCurrency'
import NoData from '../../../../component/ui/NoData'
import { updateRoute } from '../../../../store/dataRoute'
import { useDispatch } from 'react-redux'

const CurrencyExchangeRates = () => {
    const dispatch = useDispatch()
    const [currencyList, setCurrencyList] = useState([])
    const [loading, setLoading] = useState(false)
    const [openAddModal, setOpenAddModal] = useState(false)
    const [totalData, setTotalData] = useState(0)
    const [isEdit, setIsEdit] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)

    const mappingRoute = [
    {
      label: '/',
      route: null
    },
    {
      label: 'Common & Global Settings',
      route: null
    },
    {
      label: 'Currency Exchange Rates',
      route: null
    },
  ]

    // Filter states
    const [filters, setFilters] = useState({
        from: '',
        to: ''
    })

    const [params, setParams] = useState({
        'page[num]': 1,
        'page[limit]': 20,
    })

    const fetchCurrencies = async () => {
        setLoading(true)
        try {
            // Construct params with filters
            const queryParams = {
                ...params,
                ...(filters.from && { 'filter[currency_code]': filters.from }),
                ...(filters.to && { 'filter[currency_code_target]': filters.to })
            }

            const response = await getAllCurrencyList(queryParams)
            if (response.success) {
                setCurrencyList(response.data)
                setTotalData(response.meta?.pagination?.total || 0)
            }
        } catch (error) {
            console.error(error)
            notifications.show({
                title: 'Error',
                message: 'Failed to fetch currency rates',
                color: 'red'
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchCurrencies()
        dispatch(updateRoute({ 'data': mappingRoute }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params])

    const handleApplyFilter = () => {
        setParams(prev => ({ ...prev, 'page[num]': 1 })) // Reset to page 1
        fetchCurrencies()
    }

    const handleClearFilter = () => {
        setFilters({ from: '', to: '' })
        setParams(prev => ({ ...prev, 'page[num]': 1 }))
    }

    const handleEdit = (item) => {
        setIsEdit(true)
        setSelectedItem(item)
        setOpenAddModal(true)
    }

    const handleDelete = (item) => {
        modalDeleteData('Currency', item.id, `${item.attributes.currency_code} to ${item.attributes.currency_code_target}`, deleteCurrency, fetchCurrencies)
    }

    const handleChangePage = (val) => {
        setParams(prev => ({ ...prev, 'page[num]': val }))
    }

    const rows = currencyList.map((item) => (
        <Table.Tr key={item.id}>
            <Table.Td>{item.attributes.id}</Table.Td>
            <Table.Td>{item.attributes.currency_code}</Table.Td>
            <Table.Td>{item.attributes.currency_code_target}</Table.Td>
            <Table.Td>{item.attributes.value}</Table.Td>
            <Table.Td>
                <Group gap="sm">
                    <Tooltip label="Edit">
                        <ActionIcon color="blue" variant="subtle" onClick={() => handleEdit(item)}>
                            <IconEdit size={24} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Delete">
                        <ActionIcon color="red" variant="subtle" onClick={() => handleDelete(item)}>
                            <IconTrash size={24} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Table.Td>
        </Table.Tr>
    ))

    return (
        <AuthLayout>
            <Box my={20}>
                <Box>
                    <Flex justify='space-between' align="center" mb={30}>
                        <Text className={classes.titleLayout}>Currency Exchange Rates</Text>
                        <Group gap='sm' align='center'>
                            <TextInput
                                placeholder="From Currency (e.g. USD)"
                                value={filters.from}
                                onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                                size='xs'
                            />
                            <TextInput
                                placeholder="To Currency (e.g. IDR)"
                                value={filters.to}
                                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                                size='xs'
                            />
                            <Button size='xs' variant="outline" onClick={handleApplyFilter} leftSection={<IconSearch size={14} />}>
                                Apply
                            </Button>
                            {(filters.from || filters.to) && (
                                <Button size='xs' variant="subtle" color="red" onClick={handleClearFilter}>
                                    Clear
                                </Button>
                            )}
                            <Button size='xs' leftSection={<IconPlus size={14} />} onClick={() => {
                                setIsEdit(false)
                                setSelectedItem(null)
                                setOpenAddModal(true)
                            }}>
                                Add New Currency
                            </Button>
                        </Group>
                    </Flex>
                </Box>

                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data Found: </Text>
                        <Text fz={12} fw='bold'>{totalData} Currencies </Text>
                    </Group>

                    <Box pos="relative">
                        <LoadingOverlay visible={loading} overlayProps={{ radius: "sm", blur: 2 }} />
                        <Table stickyHeader striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>ID</Table.Th>
                                    <Table.Th>From</Table.Th>
                                    <Table.Th>To</Table.Th>
                                    <Table.Th>Value</Table.Th>
                                    <Table.Th>Action</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.length > 0 ? rows : (
                                    !loading && <Table.Tr>
                                        <Table.Td colSpan={5}>
                                            <NoData />
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Box>
                </Box>

                <Flex justify='end'>
                    <Pagination
                        total={Math.ceil(totalData / params['page[limit]']) || 1}
                        value={params['page[num]']}
                        onChange={handleChangePage}
                        size='sm'
                    />
                </Flex>
            </Box>

            <ModalCurrency
                opened={openAddModal}
                onClose={() => setOpenAddModal(false)}
                isEdit={isEdit}
                data={selectedItem}
                onSuccess={fetchCurrencies}
            />
        </AuthLayout>
    )
}

export default CurrencyExchangeRates
